import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Schedule
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { clienteService } from '../../services/api';
import { agendamentoService } from '../../services/api';

// =====================================================
// INTERFACES E TIPOS
// =====================================================

interface Agendamento {
  id: number;
  cliente_id: number;
  cliente_nome: string;
  clienteNome: string;
  cliente_sexo?: string | number;
  usuario_id: number;
  data: string;
  hora: string;
  duracao: number;
  tipo: string;
  status: string;
  observacoes?: string;
  cor: string;
}

interface Cliente {
  id: number;
  nome: string;
  email: string;
  telefone?: string;
  sexo?: string | number;
  data_nascimento?: string | Date;
}

interface AgendamentoFormData {
  cliente_id: string;
  data: string;
  hora: string;
  duracao: number;
  tipo: string;
  status: string;
  observacoes: string;
  cor: string;
}

interface DiaCalendario {
  data: Date;
  agendamentos: Agendamento[];
  isHoje: boolean;
  isOutroMes: boolean;
}

// As cores dos cards são definidas exclusivamente pelo campo 'cor' vindo do backend.

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const AgendamentoCalendarioFuncional: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery('(max-width:430px)'); // Detectar telas < 430px
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados principais
  const [dataAtual, setDataAtual] = useState(new Date());
  const [agendamentos, setAgendamentos] = useState<Record<string, Agendamento[]>>({});
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [modalAberto, setModalAberto] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState<Agendamento | null>(null);
  const [modalAvisoDataPassada, setModalAvisoDataPassada] = useState(false);
  const [modalConfirmacaoDragDrop, setModalConfirmacaoDragDrop] = useState(false);
  const [dadosDragDrop, setDadosDragDrop] = useState<{agendamento: Agendamento, novaData: string} | null>(null);
  const [modalAgendamentosHoje, setModalAgendamentosHoje] = useState(false);
  const [formData, setFormData] = useState<AgendamentoFormData>({
    cliente_id: '',
    data: new Date().toISOString().split('T')[0],
    hora: '09:00',
    duracao: 60,
    tipo: 'Consulta',
    status: 'Agendado',
    observacoes: '',
    cor: '#2196F3' // Azul padrão
  });

  // Carregar dados do backend
  useEffect(() => {
    carregarDadosCompletos();
  }, [user]);

  const carregarDadosCompletos = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 [DEBUG] Iniciando carregamento de agendamentos...');
      console.log('🔍 [DEBUG] Usuário:', user);
      
      // Usar o serviço de API que já tem autenticação configurada
      const response = await agendamentoService.listar({ id_usuario: user?.id || 1 });
      
      console.log('🔍 [DEBUG] Resposta completa da API:', response);
      console.log('🔍 [DEBUG] response.data:', response.data);
      
      // Verificar se a resposta tem a estrutura correta
      const agendamentosData = response.data?.data || response.data || [];
      
      console.log('🔍 [DEBUG] agendamentosData extraído:', agendamentosData);
      console.log('🔍 [DEBUG] É array?', Array.isArray(agendamentosData));
      console.log('🔍 [DEBUG] Quantidade:', agendamentosData.length);
      
      if (Array.isArray(agendamentosData) && agendamentosData.length > 0) {
        // Transformar dados do backend para o formato esperado pelo frontend
        const agendamentosTransformados = agendamentosData.map((item: any) => {
          console.log('🔍 [DEBUG] Item do backend:', item);
          // Usar cor enviada pelo backend; se inexistente, usar azul padrão
          const cor = item.cor || '#2196F3';
          
          // Garantir data no formato YYYY-MM-DD SEM criar Date a partir de string (evita fuso)
          let dataFormatada: string;
          if (typeof item.data === 'string' && item.data.length >= 10) {
            dataFormatada = item.data.slice(0, 10);
          } else if (typeof item.data_compromisso === 'string' && item.data_compromisso.length >= 10) {
            dataFormatada = item.data_compromisso.slice(0, 10);
          } else if (item.data instanceof Date) {
            dataFormatada = formatarData(item.data);
          } else {
            dataFormatada = formatarData(new Date());
          }
          
          console.log('🔍 [DEBUG] Data formatada final:', dataFormatada);
          
          return {
            id: item.id,
            cliente_id: item.cliente_id || item.id_cliente,
            cliente_nome: item.cliente_nome || 'Cliente não informado',
            clienteNome: item.cliente_nome || 'Cliente não informado',
            cliente_sexo: item.cliente_sexo, // campo opcional; não usado para cor
            usuario_id: item.usuario_id || item.id_usuario,
            data: dataFormatada, // Usar data formatada consistentemente
            hora: (typeof item.hora === 'string' && item.hora.length >= 5)
              ? item.hora.substring(0, 5)
              : (typeof item.hora_compromisso === 'string' && item.hora_compromisso.length >= 5
                ? item.hora_compromisso.substring(0, 5)
                : '09:00'),
            duracao: (typeof item.duracao === 'number')
              ? item.duracao
              : (typeof item.duracao_minutos === 'number' ? item.duracao_minutos : 60),
            tipo: item.tipo || item.tipo_sessao || 'Consulta',
            status: 'Agendado',
            observacoes: item.observacoes || '',
            cor: cor
          };
        });

        console.log('🔍 [DEBUG] Agendamentos transformados:', agendamentosTransformados);

        // Agrupar agendamentos por data
        const agendamentosAgrupados = agendamentosTransformados.reduce((acc: any, agendamento: any) => {
          const data = agendamento.data;
          if (!acc[data]) {
            acc[data] = [];
          }
          acc[data].push(agendamento);
          return acc;
        }, {});

        console.log('🔍 [DEBUG] Agendamentos agrupados:', agendamentosAgrupados);
        setAgendamentos(agendamentosAgrupados);
      } else {
        console.log('🔍 [DEBUG] Nenhum agendamento encontrado ou dados inválidos');
        setAgendamentos({});
      }

      // Carregar clientes
      const clientesResponse = await clienteService.listar();
      console.log('🔍 [DEBUG] Resposta clientes:', clientesResponse);
      
      if (clientesResponse.data?.data) {
        setClientes(clientesResponse.data.data);
        console.log('🔍 [DEBUG] Clientes carregados:', clientesResponse.data.data.length);
      }
    } catch (error: any) {
      console.error('❌ [ERROR] Erro ao carregar dados:', error);
      setError('Erro ao carregar agendamentos. Tente recarregar a página.');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FUNÇÕES UTILITÁRIAS
  // =====================================================

  const formatarData = (data: Date): string => {
    // Formatar data local sem converter para UTC (evita dia -1)
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  const obterNomeMes = (data: Date): string => {
    return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const calcularHorarioFim = (horarioInicio: string, duracao: number): string => {
    const [horas, minutos] = horarioInicio.split(':').map(Number);
    const inicioEmMinutos = horas * 60 + minutos;
    const fimEmMinutos = inicioEmMinutos + duracao;
    
    const horasFim = Math.floor(fimEmMinutos / 60);
    const minutosFim = fimEmMinutos % 60;
    
    return `${horasFim.toString().padStart(2, '0')}:${minutosFim.toString().padStart(2, '0')}`;
  };

  const gerarDias = () => {
    const dias: DiaCalendario[] = [];
    const hoje = new Date();
    const ano = dataAtual.getFullYear();
    const mes = dataAtual.getMonth();
    
    console.log('🔍 [DEBUG] Gerando dias para:', { ano, mes: mes + 1 });
    console.log('🔍 [DEBUG] Agendamentos disponíveis:', agendamentos);
    console.log('🔍 [DEBUG] Chaves dos agendamentos:', Object.keys(agendamentos));
    
    // Primeiro dia do mês
    const primeiroDia = new Date(ano, mes, 1);
    const diaSemana = primeiroDia.getDay();
    const diasAnteriores = diaSemana === 0 ? 6 : diaSemana - 1;
    
    // Adicionar dias do mês anterior
    for (let i = diasAnteriores; i > 0; i--) {
      const dia = new Date(ano, mes, 1 - i);
      if (dia.getDay() !== 0) {
        const dataFormatada = formatarData(dia);
        console.log('🔍 [DEBUG] Dia anterior:', dataFormatada, 'Agendamentos:', agendamentos[dataFormatada]?.length || 0);
        dias.push({
          data: dia,
          agendamentos: agendamentos[dataFormatada] || [],
          isHoje: false,
          isOutroMes: true
        });
      }
    }
    
    // Adicionar dias do mês atual
    const ultimoDia = new Date(ano, mes + 1, 0).getDate();
    for (let dia = 1; dia <= ultimoDia; dia++) {
      const dataCompleta = new Date(ano, mes, dia);
      if (dataCompleta.getDay() !== 0) {
        const isHoje = 
          dataCompleta.getDate() === hoje.getDate() &&
          dataCompleta.getMonth() === hoje.getMonth() &&
          dataCompleta.getFullYear() === hoje.getFullYear();
        
        const dataFormatada = formatarData(dataCompleta);
        console.log('🔍 [DEBUG] Dia atual:', dataFormatada, 'Agendamentos:', agendamentos[dataFormatada]?.length || 0);
        
        dias.push({
          data: dataCompleta,
          agendamentos: agendamentos[dataFormatada] || [],
          isHoje,
          isOutroMes: false
        });
      }
    }
    
    // Completar com dias do próximo mês
    const totalDias = dias.length;
    const diasRestantes = 36 - totalDias;
    
    for (let i = 1; i <= diasRestantes + 7; i++) {
      const dia = new Date(ano, mes + 1, i);
      if (dia.getDay() !== 0 && dias.length < 36) {
        const dataFormatada = formatarData(dia);
        console.log('🔍 [DEBUG] Dia próximo:', dataFormatada, 'Agendamentos:', agendamentos[dataFormatada]?.length || 0);
        dias.push({
          data: dia,
          agendamentos: agendamentos[dataFormatada] || [],
          isHoje: false,
          isOutroMes: true
        });
      }
    }
    
    console.log('🔍 [DEBUG] Total de dias gerados:', dias.length);
    console.log('🔍 [DEBUG] Dias com agendamentos:', dias.filter(d => d.agendamentos.length > 0).length);
    
    return dias;
  };

  // =====================================================
  // FUNÇÕES DE NAVEGAÇÃO
  // =====================================================

  const navegarMes = (direcao: 'anterior' | 'proximo') => {
    setDataAtual(prev => {
      const novaData = new Date(prev);
      if (direcao === 'anterior') {
        novaData.setMonth(prev.getMonth() - 1);
      } else {
        novaData.setMonth(prev.getMonth() + 1);
      }
      return novaData;
    });
  };

  const irParaHoje = () => {
    setDataAtual(new Date());
    setModalAgendamentosHoje(true);
  };

  // Função para obter agendamentos de hoje ordenados por hora
  const obterAgendamentosHoje = () => {
    const hoje = new Date();
    const dataHoje = formatarData(hoje);
    const agendamentosHoje = agendamentos[dataHoje] || [];
    
    // Ordenar por hora
    return agendamentosHoje.sort((a, b) => {
      const horaA = a.hora || '00:00';
      const horaB = b.hora || '00:00';
      return horaA.localeCompare(horaB);
    });
  };

  // =====================================================
  // FUNÇÕES DE AGENDAMENTO
  // =====================================================

  const abrirModal = (agendamento?: Agendamento, dataSelecionada?: string) => {
    // Verificar se é um novo agendamento para data passada
    if (!agendamento && dataSelecionada) {
      // Criar novas instâncias para não modificar as originais
      const hoje = new Date();
      const dataEscolhida = new Date(dataSelecionada);
      
      // Comparar apenas as datas no formato YYYY-MM-DD
      const hojeStr = hoje.toISOString().split('T')[0];
      const dataEscolhidaStr = dataEscolhida.toISOString().split('T')[0];
      
      // Bloquear apenas datas ANTERIORES à atual (permitir hoje e futuras)
      if (dataEscolhidaStr < hojeStr) {
        setModalAvisoDataPassada(true);
        return;
      }
    }

    if (agendamento) {
      setAgendamentoSelecionado(agendamento);
      setFormData({
        cliente_id: agendamento.cliente_id.toString(),
        data: agendamento.data,
        hora: agendamento.hora,
        duracao: agendamento.duracao,
        tipo: agendamento.tipo,
        status: agendamento.status,
        observacoes: agendamento.observacoes || '',
        cor: agendamento.cor || '#2196F3'
      });
    } else {
      setAgendamentoSelecionado(null);
      setFormData({
        cliente_id: '',
        data: dataSelecionada || new Date().toISOString().split('T')[0],
        hora: '09:00',
        duracao: 60,
        tipo: 'Consulta',
        status: 'Agendado',
        observacoes: '',
        cor: '#2196F3'
      });
    }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setAgendamentoSelecionado(null);
    setError('');
  };

  const salvarAgendamento = async () => {
    console.log('🚀 [DEBUG] ===== FUNÇÃO SALVAR AGENDAMENTO CHAMADA =====');
    console.log('🚀 [DEBUG] Função salvarAgendamento chamada');
    console.log('🚀 [DEBUG] FormData atual:', formData);
    console.log('🚀 [DEBUG] Clientes disponíveis:', clientes);
    
    try {
      setLoading(true);
      setError('');

      if (!formData.cliente_id) {
        console.log('❌ [ERROR] Cliente não selecionado');
        setError('Selecione um cliente');
        setLoading(false);
        return;
      }

      // Debug detalhado da comparação de cliente_id
      console.log('🔍 [DEBUG] ===== ANÁLISE DETALHADA DO CLIENTE_ID =====');
      console.log('🔍 [DEBUG] formData.cliente_id:', formData.cliente_id);
      console.log('🔍 [DEBUG] typeof formData.cliente_id:', typeof formData.cliente_id);
      console.log('🔍 [DEBUG] parseInt(formData.cliente_id):', parseInt(formData.cliente_id));
      console.log('🔍 [DEBUG] Clientes disponíveis (IDs e tipos):');
      clientes.forEach((c, index) => {
        console.log(`🔍 [DEBUG] Cliente ${index}: id=${c.id}, typeof=${typeof c.id}, nome=${c.nome}`);
      });

      // Tentar diferentes formas de comparação
      const clienteComParseInt = clientes.find(c => c.id === parseInt(formData.cliente_id));
      const clienteComString = clientes.find(c => c.id.toString() === formData.cliente_id.toString());
      const clienteComNumero = clientes.find(c => Number(c.id) === Number(formData.cliente_id));
      
      console.log('🔍 [DEBUG] Resultado comparação parseInt:', clienteComParseInt);
      console.log('🔍 [DEBUG] Resultado comparação string:', clienteComString);
      console.log('🔍 [DEBUG] Resultado comparação Number:', clienteComNumero);

      // Usar a comparação mais robusta
      const cliente = clienteComParseInt || clienteComString || clienteComNumero;
      
      if (!cliente) {
        console.log('❌ [ERROR] Cliente não encontrado na lista após todas as tentativas');
        console.log('❌ [ERROR] formData.cliente_id final:', formData.cliente_id);
        console.log('❌ [ERROR] IDs disponíveis:', clientes.map(c => c.id));
        setError('Cliente não encontrado');
        setLoading(false);
        return;
      }

      console.log('✅ [SUCCESS] Cliente encontrado:', cliente);
      
      // Preparar dados para o backend
      const dadosBackend = {
        data: formData.data,
        hora: formData.hora,
        duracao: formData.duracao,
        tipo: formData.tipo,
        cliente_id: parseInt(formData.cliente_id),
        usuario_id: user?.id || 1,
        observacoes: formData.observacoes,
        cor: formData.cor
      };

      console.log('🔍 [DEBUG] Dados enviados para backend:', dadosBackend);
      console.log('🔍 [DEBUG] FormData original:', formData);
      console.log('🔍 [DEBUG] Cliente selecionado:', cliente);
      console.log('🔍 [DEBUG] Agendamento selecionado:', agendamentoSelecionado);

      let response;
      
      if (agendamentoSelecionado) {
        // Editando agendamento existente
        console.log('📝 [DEBUG] Atualizando agendamento existente ID:', agendamentoSelecionado.id);
        response = await agendamentoService.atualizar(agendamentoSelecionado.id, dadosBackend);
      } else {
        // Criando novo agendamento
        console.log('➕ [DEBUG] Criando novo agendamento...');
        response = await agendamentoService.criar(dadosBackend);
      }

      console.log('📥 [DEBUG] Resposta completa do backend:', response);
      console.log('📥 [DEBUG] response.data:', response.data);
      
      if (!response.data.success) {
        console.log('❌ [ERROR] Falha ao salvar agendamento:', response.data.message);
        setError(response.data.message || 'Erro ao salvar agendamento');
      } else {
        // Atualizar estado local com dados do backend
        const novoAgendamento: Agendamento = {
          id: agendamentoSelecionado?.id || response.data.data?.id || Date.now(),
          cliente_id: parseInt(formData.cliente_id),
          cliente_nome: cliente.nome,
          clienteNome: cliente.nome,
          cliente_sexo: cliente.sexo, // Adicionar campo sexo
          usuario_id: user?.id || 1,
          data: formData.data,
          hora: formData.hora,
          duracao: formData.duracao,
          tipo: formData.tipo,
          status: 'Agendado',
          observacoes: formData.observacoes,
          cor: formData.cor
        };

        setAgendamentos(prev => {
          const novosAgendamentos = { ...prev };
          const dataKey = formData.data;
          
          if (agendamentoSelecionado) {
            // Editando - remover o antigo de todas as datas
            Object.keys(novosAgendamentos).forEach(data => {
              novosAgendamentos[data] = novosAgendamentos[data].filter(
                a => a.id !== agendamentoSelecionado.id
              );
            });
          }

          if (!novosAgendamentos[dataKey]) {
            novosAgendamentos[dataKey] = [];
          }

          const agendamentoAtualizado = { ...novoAgendamento, data: dataKey };
        
          // Verificar se já existe para evitar duplicação
          const jaExiste = novosAgendamentos[dataKey].some(a => a.id === agendamentoAtualizado.id);
          if (!jaExiste) {
            novosAgendamentos[dataKey].push(agendamentoAtualizado);
          }

          return novosAgendamentos;
        });

        fecharModal();
        
        // Forçar refresh completo da agenda
        console.log('🔄 [DEBUG] Forçando refresh completo da agenda...');
        await carregarDadosCompletos();
        console.log('✅ [DEBUG] Refresh completo da agenda finalizado');
      }
    } catch (error: any) {
      console.error('Erro ao salvar agendamento:', error.message);
      setError(error.response?.data?.message || 'Erro ao salvar agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const excluirAgendamento = async (id: number) => {
    try {
      setLoading(true);
      
      // Chamar o backend para excluir permanentemente
      await agendamentoService.excluir(id);
      
      // Remover do estado local após sucesso no backend
      setAgendamentos(prev => {
        const novosAgendamentos = { ...prev };
        Object.keys(novosAgendamentos).forEach(data => {
          novosAgendamentos[data] = novosAgendamentos[data].filter(a => a.id !== id);
        });
        return novosAgendamentos;
      });
      
      fecharModal();
      
      // Opcional: mostrar mensagem de sucesso
      console.log('✅ Agendamento excluído com sucesso');
      
    } catch (error) {
      console.error('❌ Erro ao excluir agendamento:', error);
      // Opcional: mostrar mensagem de erro para o usuário
      alert('Erro ao excluir agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // DRAG AND DROP
  // =====================================================

  const handleDragStart = (e: React.DragEvent, agendamento: Agendamento) => {
    console.log('🚀 [DEBUG] Iniciando drag do agendamento:', agendamento.id, agendamento.clienteNome);
    e.dataTransfer.setData('agendamento', JSON.stringify(agendamento));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    console.log('👆 [DEBUG] Drag over detectado');
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, novaData: string) => {
    e.preventDefault();
    
    try {
      const agendamento: Agendamento = JSON.parse(e.dataTransfer.getData('agendamento'));
      
      if (agendamento.data === novaData) return;

      // Verificar se está movendo para uma data anterior
      const hoje = new Date();
      const dataNovaObj = new Date(novaData);
      const dataAgendamentoObj = new Date(agendamento.data);
      
      // Zerar as horas para comparar apenas as datas
      hoje.setHours(0, 0, 0, 0);
      dataNovaObj.setHours(0, 0, 0, 0);
      dataAgendamentoObj.setHours(0, 0, 0, 0);
      
      // Se está movendo para uma data anterior ao agendamento original, pedir confirmação
      if (dataNovaObj.getTime() < dataAgendamentoObj.getTime()) {
        setDadosDragDrop({ agendamento, novaData });
        setModalConfirmacaoDragDrop(true);
        return;
      }

      // Continuar com o processo normal de drag and drop
      await executarDragDrop(agendamento, novaData);
    } catch (error: any) {
      console.error('Erro no drag and drop:', error);
      setError('Erro ao mover agendamento. Tente novamente.');
    }
  };

  const executarDragDrop = async (agendamento: Agendamento, novaData: string) => {
    // Primeiro, atualizar localmente para resposta imediata
    setAgendamentos(prev => {
      const novosAgendamentos = { ...prev };
      
      // Remover da data original
      if (novosAgendamentos[agendamento.data]) {
        novosAgendamentos[agendamento.data] = novosAgendamentos[agendamento.data].filter(
          a => a.id !== agendamento.id
        );
        
        // Se o array ficou vazio, remover a chave
        if (novosAgendamentos[agendamento.data].length === 0) {
          delete novosAgendamentos[agendamento.data];
        }
      }
      
      // Adicionar na nova data
      if (!novosAgendamentos[novaData]) {
        novosAgendamentos[novaData] = [];
      }
      
      const agendamentoAtualizado = { ...agendamento, data: novaData };
      
      // Verificar se já existe para evitar duplicação
      const jaExiste = novosAgendamentos[novaData].some(a => a.id === agendamento.id);
      if (!jaExiste) {
        novosAgendamentos[novaData].push(agendamentoAtualizado);
      }

      return novosAgendamentos;
    });

    // Depois, tentar sincronizar com o backend (sem bloquear a UI)
    try {
      const dadosBackend = {
        data: novaData,
        hora: agendamento.hora,
        duracao: agendamento.duracao,
        tipo: agendamento.tipo,
        cliente_id: agendamento.cliente_id,
        usuario_id: agendamento.usuario_id,
        observacoes: agendamento.observacoes,
        cor: agendamento.cor
      };

      console.log('🔄 [DEBUG] Dados enviados para backend:', dadosBackend);
      console.log('🔄 [DEBUG] URL:', `https://trajetus.net/api/agendamentos/${agendamento.id}`);
      
      const response = await agendamentoService.atualizar(agendamento.id, dadosBackend);
      
      console.log('📥 [DEBUG] Resposta do backend:', response.data);
      
      if (!response.data.success) {
        console.warn('⚠️ [WARN] Falha ao sincronizar com backend:', response.data.message);
        // Não reverter a mudança local, apenas avisar no console
      } else {
        console.log('✅ [SUCCESS] Agendamento atualizado com sucesso no backend');
      }
    } catch (backendError: any) {
      console.error('❌ [ERROR] Erro ao sincronizar drag-and-drop com backend:', backendError);
      console.error('❌ [ERROR] Detalhes do erro:', backendError.response?.data);
      // Continuar funcionando localmente mesmo se o backend falhar
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  const dias = gerarDias();

  const truncarNome = (nome: string) => {
    if (!nome) return '';
    
    // Se a tela for menor que 430px, mostrar apenas o primeiro nome
    if (isSmallScreen) {
      const primeiroNome = nome.split(' ')[0];
      return primeiroNome;
    }
    
    // Para telas >= 430px, mostrar os 2 primeiros nomes
    const nomes = nome.split(' ');
    if (nomes.length >= 2) {
      return `${nomes[0]} ${nomes[1]}`;
    }
    
    // Se só tem 1 nome, retornar ele
    return nomes[0] || nome;
  };

  return (
    <Box sx={{ 
      p: isMobile ? 1 : 2, 
      height: '100vh', 
      overflow: 'hidden',
      backgroundColor: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
        <Stack 
          direction={isMobile ? "column" : "row"} 
          justifyContent="space-between" 
          alignItems="center"
          spacing={isMobile ? 1 : 0}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.light + '20'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                textAlign: isMobile ? 'center' : 'left'
              }}
            >
              Agendamentos
            </Typography>
          </Stack>
          
          <Stack 
            direction="row" 
            spacing={isMobile ? 0.5 : 1} 
            alignItems="center"
          >
            <IconButton 
              onClick={() => navegarMes('anterior')}
              size={isMobile ? "small" : "medium"}
            >
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              sx={{ 
                minWidth: isMobile ? 120 : 200, 
                textAlign: 'center', 
                textTransform: 'capitalize',
                fontWeight: 'bold'
              }}
            >
              {obterNomeMes(dataAtual)}
            </Typography>
            
            <IconButton 
              onClick={() => navegarMes('proximo')}
              size={isMobile ? "small" : "medium"}
            >
              <ChevronRightIcon />
            </IconButton>
            
            <Button
              variant="outlined"
              startIcon={!isMobile ? <TodayIcon /> : undefined}
              onClick={irParaHoje}
              size={isMobile ? "small" : "medium"}
              sx={{ ml: isMobile ? 0.5 : 2 }}
            >
              Hoje
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Calendário */}
      <Paper elevation={2} sx={{ 
        flexGrow: 1, 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
        height: 'calc(100vh - 200px)'
      }}>
        {/* Cabeçalho dos dias da semana */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(6, 1fr)', 
          borderBottom: '2px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          {(isMobile ? 
            [{ key: 'seg', label: 'S' }, { key: 'ter', label: 'T' }, { key: 'qua', label: 'Q' }, { key: 'qui', label: 'Q' }, { key: 'sex', label: 'S' }, { key: 'sab', label: 'S' }] : 
            [{ key: 'seg', label: 'Seg' }, { key: 'ter', label: 'Ter' }, { key: 'qua', label: 'Qua' }, { key: 'qui', label: 'Qui' }, { key: 'sex', label: 'Sex' }, { key: 'sab', label: 'Sáb' }]
          ).map((dia) => (
            <Typography
              key={dia.key}
              variant={isMobile ? "caption" : "subtitle2"}
              sx={{
                p: isMobile ? 0.5 : 1,
                textAlign: 'center',
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              {dia.label}
            </Typography>
          ))}
        </Box>

        {/* Grid do calendário */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(6, 1fr)', 
          gridTemplateRows: 'repeat(6, 1fr)',
          flexGrow: 1,
          overflow: 'auto',
          gap: isMobile ? '2px' : '1px',
          height: '100%',
          p: isMobile ? 0.5 : 1
        }}>
          {dias.map((dia, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{
                height: isMobile ? '80px' : '100%',
                minHeight: isMobile ? '80px' : '120px',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f9f9f9' },
                opacity: dia.isOutroMes ? 0.5 : 1,
                backgroundColor: dia.isHoje ? '#e3f2fd' : 'transparent',
                border: isMobile ? '1px solid #ddd' : '1px solid #e0e0e0'
              }}
              onClick={() => abrirModal(undefined, formatarData(dia.data))}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, formatarData(dia.data))}
            >
              <CardContent sx={{ 
                p: isMobile ? 0.5 : 1, 
                pt: isMobile ? 0.25 : 0.5, 
                pb: isMobile ? 0.25 : 0.5, 
                flexGrow: 1, 
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: dia.isHoje ? 'bold' : 'normal',
                    color: dia.isHoje ? 'primary.main' : 'text.primary',
                    mb: 0.5
                  }}
                >
                  {dia.data.getDate()}
                </Typography>

                <Stack spacing={isMobile ? 0.25 : 0.5} sx={{ 
                  maxHeight: isMobile ? '60px' : '90px', 
                  overflow: 'auto',
                  mb: isMobile ? -1.5 : 0, 
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#ccc',
                    borderRadius: '2px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#999',
                  }
                }}>
                  {dia.agendamentos.map((agendamento) => (
                    <Box
                      key={`${agendamento.id}-${formatarData(dia.data)}`}
                      sx={{
                        backgroundColor: agendamento.cor,
                        color: 'white',
                        borderRadius: '16px',
                        px: isMobile ? 0.5 : 1,
                        py: isMobile ? 0.25 : 0.5,
                        fontSize: isMobile ? '0.6rem' : '0.7rem',
                        cursor: 'pointer',
                        minHeight: isMobile ? '24px' : '20px',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'center' : 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('🖱️ [DEBUG] Clique no agendamento:', agendamento.id, agendamento.clienteNome);
                        abrirModal(agendamento);
                      }}
                      onMouseDown={(e) => {
                        console.log('🖱️ [DEBUG] Mouse down no agendamento:', agendamento.id);
                      }}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, agendamento)}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: isMobile ? '0.55rem' : '0.65rem',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%'
                        }}
                      >
                        {isSmallScreen ? truncarNome(agendamento.clienteNome) : `${agendamento.hora} - ${truncarNome(agendamento.clienteNome)}`}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>

      {/* Modal de Agendamento */}
      <Dialog 
        open={modalAberto} 
        onClose={fecharModal} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          {agendamentoSelecionado ? 'Editar Agendamento' : 'Novo Agendamento'}
          <IconButton onClick={fecharModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Cliente</InputLabel>
              <Select
                value={formData.cliente_id}
                onChange={(e) => setFormData(prev => ({ ...prev, cliente_id: e.target.value }))}
                label="Cliente"
              >
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Data"
                type="date"
                value={formData.data}
                onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Horário"
                type="time"
                value={formData.hora}
                onChange={(e) => setFormData(prev => ({ ...prev, hora: e.target.value }))}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Duração (min)"
                type="number"
                value={formData.duracao}
                onChange={(e) => setFormData(prev => ({ ...prev, duracao: parseInt(e.target.value) }))}
                sx={{ flex: 1 }}
              />
              
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Tipo de Sessão</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                  label="Tipo de Sessão"
                >
                  <MenuItem value="Consulta">Consulta</MenuItem>
                  <MenuItem value="Avaliação">Avaliação</MenuItem>
                  <MenuItem value="Retorno">Retorno</MenuItem>
                  <MenuItem value="Supervisão">Supervisão</MenuItem>
                  <MenuItem value="Outro">Outro</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Campo de seleção de Cor do agendamento */}
            <FormControl fullWidth>
              <InputLabel>Cor</InputLabel>
              <Select
                value={formData.cor}
                label="Cor"
                onChange={(e) => setFormData(prev => ({ ...prev, cor: e.target.value }))}
              >
                <MenuItem value="#2196F3">Azul</MenuItem>
                <MenuItem value="#4CAF50">Verde</MenuItem>
                <MenuItem value="#F44336">Vermelha</MenuItem>
                <MenuItem value="#F57C00">Laranja</MenuItem>
                <MenuItem value="#795548">Marrom</MenuItem>
                <MenuItem value="#212121">Preta</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Observações"
              multiline
              rows={3}
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          {agendamentoSelecionado && (
            <Button
              color="error"
              onClick={() => excluirAgendamento(agendamentoSelecionado.id)}
              startIcon={<DeleteIcon />}
            >
              Excluir
            </Button>
          )}
          
          <Box sx={{ flex: 1 }} />
          
          <Button onClick={fecharModal}>
            Cancelar
          </Button>
          
          <Button 
            variant="contained" 
            onClick={salvarAgendamento}
            disabled={!formData.cliente_id || loading}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aviso - Data Passada */}
      <Dialog 
        open={modalAvisoDataPassada} 
        onClose={() => setModalAvisoDataPassada(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#ffebee', 
          color: '#c62828',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon />
          Aviso - Data Passada
        </DialogTitle>
        
        <DialogContent sx={{ backgroundColor: '#ffebee', pt: 2 }}>
          <Typography color="#c62828" sx={{ fontWeight: 500 }}>
            Não é possível agendar para uma data passada. Por favor, escolha uma data futura.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#ffebee', p: 3 }}>
          <Button 
            onClick={() => setModalAvisoDataPassada(false)}
            variant="contained"
            sx={{ 
              backgroundColor: '#c62828',
              '&:hover': { backgroundColor: '#b71c1c' }
            }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação - Drag and Drop para Data Anterior */}
      <Dialog 
        open={modalConfirmacaoDragDrop} 
        onClose={() => {
          setModalConfirmacaoDragDrop(false);
          setDadosDragDrop(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#ffebee', 
          color: '#c62828',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon />
          Confirmação - Mover para Data Anterior
        </DialogTitle>
        
        <DialogContent sx={{ backgroundColor: '#ffebee', pt: 2 }}>
          <Typography color="#c62828" sx={{ fontWeight: 500, mb: 2 }}>
            Você está movendo um agendamento para uma data anterior:
          </Typography>
          {dadosDragDrop && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#fff', 
              borderRadius: 1, 
              border: '2px solid #ef5350' 
            }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Cliente:</strong> {dadosDragDrop.agendamento.clienteNome}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Data original:</strong> {new Date(dadosDragDrop.agendamento.data).toLocaleDateString('pt-BR')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Nova data:</strong> {new Date(dadosDragDrop.novaData).toLocaleDateString('pt-BR')}
              </Typography>
              <Typography variant="body2">
                <strong>Horário:</strong> {dadosDragDrop.agendamento.hora}
              </Typography>
            </Box>
          )}
          <Typography color="#c62828" sx={{ fontWeight: 500, mt: 2 }}>
            Deseja confirmar esta alteração?
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#ffebee', p: 3 }}>
          <Button 
            onClick={() => {
              setModalConfirmacaoDragDrop(false);
              setDadosDragDrop(null);
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#c62828',
              color: '#c62828',
              '&:hover': { 
                borderColor: '#b71c1c',
                backgroundColor: 'rgba(198, 40, 40, 0.04)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={async () => {
              if (dadosDragDrop) {
                await executarDragDrop(dadosDragDrop.agendamento, dadosDragDrop.novaData);
                setModalConfirmacaoDragDrop(false);
                setDadosDragDrop(null);
              }
            }}
            variant="contained"
            sx={{ 
              backgroundColor: '#c62828',
              '&:hover': { backgroundColor: '#b71c1c' }
            }}
          >
            Confirmar Alteração
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Agendamentos de Hoje */}
      <Dialog 
        open={modalAgendamentosHoje} 
        onClose={() => setModalAgendamentosHoje(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: theme.palette.primary.main, 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <TodayIcon />
          Agendamentos de Hoje
          <Typography variant="body2" sx={{ ml: 'auto', opacity: 0.9 }}>
            {new Date().toLocaleDateString('pt-BR')}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {obterAgendamentosHoje().length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Nenhum agendamento para hoje
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {obterAgendamentosHoje().map((agendamento, index) => (
                <React.Fragment key={agendamento.id}>
                  <ListItem 
                    sx={{ 
                      py: 2,
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setAgendamentoSelecionado(agendamento);
                      setModalAgendamentosHoje(false);
                      setModalAberto(true);
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: agendamento.cor || theme.palette.primary.main,
                        width: 40,
                        height: 40
                      }}>
                        <Schedule />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {agendamento.clienteNome || agendamento.cliente_nome}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Horário:</strong> {agendamento.hora}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Tipo:</strong> {agendamento.tipo}
                          </Typography>
                          {agendamento.observacoes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              <strong>Obs:</strong> {agendamento.observacoes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Chip 
                      label={agendamento.status}
                      size="small"
                      sx={{ 
                        backgroundColor: agendamento.cor + '20',
                        color: agendamento.cor,
                        fontWeight: 600
                      }}
                    />
                  </ListItem>
                  {index < obterAgendamentosHoje().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setModalAgendamentosHoje(false)}
            variant="outlined"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendamentoCalendarioFuncional;
